import styled, { css } from "styled-components";

export type FixtureStatusTone =
  | "live"
  | "proposed"
  | "confirmed"
  | "completed"
  | "cancelled"
  | "default";

export type FixtureActionVariant = "primary" | "secondary" | "link";

export const ShowcaseStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
`;

export const ShowcaseToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  border-radius: 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(30, 35, 42, 0.98);
  padding: 1.05rem 1.15rem;
`;

export const ShowcaseToolbarMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem 1rem;
  min-width: 0;
`;

export const ShowcaseToolbarTitle = styled.h3`
  color: var(--brand-ink);
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.25;
`;

export const ShowcaseToolbarSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.1rem;
`;

export const ToolbarBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--brand-muted);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
`;

export const ToolbarBadgeDot = styled.span`
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: #ef4444;
  box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.42);
  animation: fixture-live-pulse 1.8s infinite;

  @keyframes fixture-live-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.42);
    }

    70% {
      box-shadow: 0 0 0 0.45rem rgba(239, 68, 68, 0);
    }

    100% {
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
`;

export const ShowcaseToolbarControls = styled.div`
  display: grid;
  grid-template-columns: minmax(12rem, 1.4fr) repeat(3, minmax(9.5rem, 11.5rem)) auto;
  align-items: center;
  gap: 0.6rem;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ShowcaseSearchInput = styled.input`
  width: 100%;
  min-width: 0;
  border-radius: 0.95rem;
  border: 1px solid var(--brand-border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--brand-ink);
  font-size: 0.88rem;
  padding: 0.7rem 0.95rem;

  &::placeholder {
    color: rgba(169, 163, 154, 0.88);
  }

  &:focus {
    outline: none;
    border-color: rgba(240, 191, 86, 0.26);
    box-shadow: 0 0 0 3px rgba(240, 191, 86, 0.08);
  }
`;

export const ShowcaseFilterSelect = styled.div`
  min-width: 0;
  width: 100%;
`;

export const ShowcaseClearSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 2.65rem;
  min-width: 6.75rem;
`;

export const FixtureDaySection = styled.section`
  position: relative;
  width: 100%;
  border-radius: 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(28, 33, 41, 0.98);
  padding: 1.1rem 1.15rem;
`;

export const SectionContent = styled.div`
  position: relative;
  z-index: 1;
`;

export const FixtureResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const FixtureCardList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 30rem), 36rem));
  justify-content: start;
  align-items: stretch;
  column-gap: 1rem;
  row-gap: 1rem;
`;

export const FixtureDayCluster = styled.section`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
  padding: 0.95rem 1rem 1.05rem;
  border-radius: 1.15rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(28, 33, 41, 0.55);

  & > ${FixtureCardList} {
    width: 100%;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 30rem), 36rem));
    justify-content: start;
  }
`;

export const DayHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.65rem 1rem;
  width: 100%;
  padding-bottom: 0.15rem;
`;

export const DayMeta = styled.div`
  min-width: 0;
`;

export const DayEyebrow = styled.p`
  color: var(--brand-highlight);
  font-family: var(--font-anonymous, inherit);
  font-size: 0.75rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

export const DayTitle = styled.h3`
  color: var(--brand-ink);
  font-size: 1.12rem;
  font-weight: 700;
  line-height: 1.2;
`;

export const FixtureCountBadge = styled.div`
  color: var(--brand-muted);
  font-size: 0.8rem;
  font-weight: 600;
`;

export const GroupStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const CompetitionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem 1rem;
`;

export const CompetitionMeta = styled.div`
  min-width: 0;
`;

export const CompetitionEyebrow = styled.p`
  color: var(--brand-muted);
  font-family: var(--font-anonymous, inherit);
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const CompetitionTitle = styled.h4`
  color: var(--brand-ink);
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.3;
  margin-top: 0.2rem;
`;

export const FixtureCardShell = styled.article<{ $live?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  height: 100%;
  min-width: 0;
  width: 100%;
  border-radius: 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid
    ${({ $live }) => ($live ? "rgba(239, 68, 68, 0.95)" : "rgba(240, 191, 86, 0.55)")};
  background: rgba(18, 22, 28, 0.98);
  padding: 1.05rem 1.1rem;
  overflow: hidden;
  transition: border-color 160ms ease, background-color 160ms ease;

  &:hover {
    background: rgba(22, 26, 33, 0.98);
    border-color: rgba(255, 255, 255, 0.12);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const FixtureCardPattern = styled.div<{ $live?: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: ${({ $live }) =>
    $live
      ? "linear-gradient(135deg, rgba(239, 68, 68, 0.07), transparent 42%)"
      : "linear-gradient(135deg, rgba(240, 191, 86, 0.05), transparent 42%)"};
`;

export const FixtureCardGrid = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.9rem;
`;

export const FixtureMetaPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

export const MetaEyebrow = styled.p`
  color: var(--brand-muted);
  font-family: var(--font-anonymous, inherit);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const MetaTitle = styled.p`
  color: var(--brand-ink);
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
`;

export const MetaDateTitle = styled.p`
  color: var(--brand-ink);
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.15;
`;

export const KickoffHeadline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.7rem;
  min-width: 0;
`;

export const KickoffDivider = styled.span`
  color: rgba(196, 191, 182, 0.45);
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1;
`;

export const MetaBody = styled.p`
  color: var(--brand-ink);
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.25rem;
`;

export const MetaSubtext = styled.p`
  color: var(--brand-muted);
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  font-size: 0.78rem;
  line-height: 1.35;
`;

export const FixtureMetaRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
`;

export const FixtureKickMeta = styled.p`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  color: var(--brand-muted);
  font-size: 0.8rem;
  line-height: 1.4;
`;

export const FixtureKickMetaStrong = styled.span`
  color: var(--brand-ink);
  font-weight: 600;
`;

const statusToneStyles = {
  live: css`
    border-color: rgba(248, 113, 113, 0.35);
    background: rgba(239, 68, 68, 0.14);
    color: #fecaca;
  `,
  proposed: css`
    border-color: rgba(245, 158, 11, 0.32);
    background: rgba(245, 158, 11, 0.12);
    color: #fbbf24;
  `,
  confirmed: css`
    border-color: rgba(52, 211, 153, 0.3);
    background: rgba(16, 185, 129, 0.12);
    color: #6ee7b7;
  `,
  completed: css`
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: var(--brand-muted);
  `,
  cancelled: css`
    border-color: rgba(248, 113, 113, 0.28);
    background: rgba(239, 68, 68, 0.1);
    color: #fca5a5;
  `,
  default: css`
    border-color: rgba(240, 191, 86, 0.22);
    background: rgba(240, 191, 86, 0.1);
    color: var(--brand-highlight);
  `,
};

export const FixtureStatusPill = styled.span<{ $tone?: FixtureStatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  padding: 0.38rem 0.68rem;
  text-transform: uppercase;
  ${({ $tone = "default" }) => statusToneStyles[$tone]}
`;

export const FixtureStatusDot = styled.span<{ $live?: boolean }>`
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 999px;
  background: ${({ $live }) => ($live ? "#ef4444" : "currentColor")};
  box-shadow: ${({ $live }) => ($live ? "0 0 0 0 rgba(239, 68, 68, 0.36)" : "none")};
  animation: ${({ $live }) => ($live ? "fixture-status-pulse 1.8s infinite" : "none")};

  @keyframes fixture-status-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.36);
    }

    70% {
      box-shadow: 0 0 0 0.4rem rgba(239, 68, 68, 0);
    }

    100% {
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
`;

export const TeamPlateShell = styled.div<{ $align?: "left" | "right" }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => ($align === "right" ? "flex-end" : "flex-start")};
  gap: 0.55rem;
  min-width: 0;
  text-align: ${({ $align }) => ($align === "right" ? "right" : "left")};
`;

export const TeamLogoSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TeamTextPlate = styled.div<{ $align?: "left" | "right" }>`
  min-width: 0;
  width: 100%;
  text-align: ${({ $align }) => ($align === "right" ? "right" : "left")};
`;

export const TeamName = styled.p`
  color: var(--brand-ink);
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  min-height: 2.4em;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.2;
`;

export const TeamSchool = styled.p`
  color: var(--brand-muted);
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  min-height: 2.4em;
  font-size: 0.75rem;
  line-height: 1.2;
  margin-top: 0.2rem;
`;

export const MatchBadgeSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  min-width: 4.5rem;
  max-width: 100%;
  justify-self: center;
`;

export const MatchBadgeSurface = styled.div<{ $live?: boolean; $hasScore?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  min-width: 4.5rem;
  min-height: 3.75rem;
  border-radius: 0.95rem;
  border: 1px solid
    ${({ $live, $hasScore }) =>
      $live
        ? "rgba(248, 113, 113, 0.4)"
        : $hasScore
          ? "rgba(255, 255, 255, 0.14)"
          : "rgba(240, 191, 86, 0.28)"};
  background: ${({ $live, $hasScore }) =>
    $live
      ? "rgba(127, 29, 29, 0.55)"
      : $hasScore
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(240, 191, 86, 0.12)"};
  padding: 0.45rem 0.65rem;
`;

export const MatchBadgeInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  width: max-content;
  max-width: 100%;
  text-align: center;
`;

export const MatchBadgeLabel = styled.p`
  color: var(--brand-muted);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const MatchBadgeScoreRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: max-content;
`;

export const MatchBadgeValue = styled.p<{ $hasScore?: boolean; $side?: "home" | "away" | "center" }>`
  color: var(--brand-ink);
  white-space: nowrap;
  font-size: ${({ $hasScore }) => ($hasScore ? "1.05rem" : "0.98rem")};
  font-style: ${({ $hasScore }) => ($hasScore ? "normal" : "italic")};
  font-weight: 800;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
`;

export const MatchBadgeDivider = styled.span`
  color: var(--brand-muted);
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
`;

export const MatchupGrid = styled.div`
  display: grid;
  align-items: start;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr) max-content minmax(0, 1fr);
  min-height: 7.25rem;
  padding: 0.85rem 0.7rem;
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

export const FixtureContext = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

export const FixtureFact = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
  color: var(--brand-ink);
`;

export const FixtureFactIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  color: var(--brand-highlight);
  line-height: 0;
`;

export const FixtureFactBody = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.08rem;
`;

export const FixtureFooter = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.55rem 0.85rem;
  margin-top: auto;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
`;

export const FixtureTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem 1rem;
  min-width: 0;
`;

export const FixtureTag = styled.span<{ $tone?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--brand-ink);
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.3;
`;

export const FixtureTagLabel = styled.span`
  color: var(--brand-muted);
  font-size: 0.75rem;
  font-weight: 600;
`;

export const FixtureActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.35rem 0.75rem;
  width: 100%;
`;

const actionVariantStyles = {
  primary: css`
    border-color: transparent;
    background: var(--brand-highlight);
    color: #1b1306;
    font-weight: 800;
    padding: 0.62rem 1.05rem;
    border-radius: 0.9rem;

    &:hover {
      background: var(--brand-accent);
      color: #1b1306;
    }
  `,
  secondary: css`
    border-color: rgba(255, 255, 255, 0.16);
    background: transparent;
    color: var(--brand-ink);
    font-weight: 600;
    padding: 0.58rem 0.95rem;
    border-radius: 0.9rem;

    &:hover {
      border-color: rgba(240, 191, 86, 0.35);
      background: rgba(240, 191, 86, 0.06);
    }
  `,
  link: css`
    border-color: transparent;
    background: transparent;
    color: var(--brand-muted);
    font-size: 0.8rem;
    font-weight: 600;
    padding: 0.35rem 0.15rem;
    border-radius: 0;
    text-decoration: underline;
    text-decoration-color: rgba(196, 191, 182, 0.35);
    text-underline-offset: 0.18em;

    &:hover {
      color: var(--brand-highlight);
      text-decoration-color: rgba(240, 191, 86, 0.55);
      background: transparent;
    }
  `,
};

const actionBaseStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  font-size: 0.84rem;
  letter-spacing: 0.01em;
  transition:
    background 160ms ease,
    border-color 160ms ease,
    color 160ms ease,
    text-decoration-color 160ms ease;
`;

export const FixtureActionButton = styled.button<{ $variant?: FixtureActionVariant }>`
  ${actionBaseStyles}
  ${({ $variant = "secondary" }) => actionVariantStyles[$variant]}
`;

export const FixtureActionAnchor = styled.a<{ $variant?: FixtureActionVariant }>`
  ${actionBaseStyles}
  text-decoration: none;
  ${({ $variant = "link" }) => actionVariantStyles[$variant]}
`;

export const ResultSummary = styled.div`
  position: relative;
  z-index: 1;
  border-radius: 0.85rem;
  border: 1px solid rgba(240, 191, 86, 0.18);
  background: rgba(240, 191, 86, 0.08);
  padding: 0.6rem 0.75rem;
`;

export const ResultSummaryText = styled.p`
  color: var(--brand-ink);
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  min-height: 2.4em;
  font-size: 0.8rem;
  line-height: 1.4;
`;

export const FixtureSkeletonShell = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  min-width: 0;
  width: 100%;
  min-height: 22rem;
  border-radius: 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid rgba(240, 191, 86, 0.35);
  background: rgba(18, 22, 28, 0.98);
  padding: 1.05rem 1.1rem;
`;

export const FixtureSkeletonHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
`;

export const FixtureSkeletonMatchup = styled.div`
  display: grid;
  align-items: start;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr) max-content minmax(0, 1fr);
  min-height: 7.25rem;
  padding: 0.85rem 0.7rem;
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

export const FixtureSkeletonTeam = styled.div<{ $align?: "left" | "right" }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => ($align === "right" ? "flex-end" : "flex-start")};
  gap: 0.55rem;
  min-width: 0;
`;

export const FixtureSkeletonFacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

export const FixtureSkeletonFact = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

export const FixtureSkeletonFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.55rem 0.75rem;
  margin-top: auto;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
`;
